// covered-by: frontends/carrie/src/routes/settings/routes/__tests__/EnterpriseSettingsView.test.tsx
import { Button, Card, Flex, Heading, Text, TextField } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EnterpriseSsoSettingsPanel } from "./EnterpriseSsoSettingsPanel";

import { useUI } from "../../../contexts/ui";
import { isDemoEnterprise } from "../../../utils/auth";
import { enterpriseSlugProblem } from "../../../utils/enterpriseDuplicate";
import { useAppSelector } from "../../../utils/hooks";
import {
  useDeleteEnterprise,
  useEnterprises,
  useSwitchEnterprise,
  useUpdateEnterprise,
} from "../../../utils/queries/enterprises";

const DELETE_CONFIRM_PHRASE = "delete";

export function EnterpriseSettingsView() {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const enterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );
  const enterprisesQuery = useEnterprises();
  const updateEnterprise = useUpdateEnterprise();
  const deleteEnterprise = useDeleteEnterprise();
  const switchEnterprise = useSwitchEnterprise();

  const current = useMemo(
    () => enterprisesQuery.data?.find((e) => e._id === enterpriseId),
    [enterprisesQuery.data, enterpriseId],
  );

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Seed (and re-seed on enterprise switch) from the active enterprise.
  // Depend on `_id` only — not `current` / nameDirty — so a background
  // enterprises refetch cannot clobber in-progress edits, and so the
  // initial "" useState is never treated as "dirty" (which skipped the
  // first populate entirely).
  useEffect(() => {
    if (!current) return;
    setName(current.name);
    setSlug(current.slug ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed on switch only
  }, [current?._id]);

  const isDemo = current?.isDemo === true || isDemoEnterprise();
  const fallbackEnterprise = useMemo(
    () => enterprisesQuery.data?.find((e) => e._id !== enterpriseId),
    [enterprisesQuery.data, enterpriseId],
  );
  const nameDirty = current != null && name.trim() !== current.name;
  const slugDirty =
    current != null && slug.trim() !== (current.slug ?? "").trim();
  const generalDirty = nameDirty || slugDirty;
  const slugProblem = enterpriseSlugProblem(slug);
  // `enterpriseSlugProblem` passes an empty slug (nothing to validate), but the
  // update schema requires a non-empty kebab-case slug. Clearing the field has
  // to block Save the way RenameEnterpriseDialog does, or the PATCH 400s and
  // takes any name edit in the same request down with it.
  const slugCleared = slugDirty && slug.trim().length === 0;
  const slugInvalid = slugProblem != null || slugCleared;
  const canSaveGeneral =
    generalDirty &&
    name.trim().length > 0 &&
    !slugInvalid &&
    !savingGeneral &&
    !!enterpriseId;
  const canDelete =
    deleteConfirm.trim().toLowerCase() === DELETE_CONFIRM_PHRASE &&
    !!enterpriseId &&
    !!fallbackEnterprise &&
    !deleting;

  async function handleSaveGeneral() {
    if (!enterpriseId || !canSaveGeneral) return;
    setSavingGeneral(true);
    try {
      const updates: { name?: string; slug?: string } = {};
      if (nameDirty) updates.name = name.trim();
      if (slugDirty) updates.slug = slug.trim();
      await updateEnterprise.mutateAsync({ enterpriseId, ...updates });
      notify({
        variant: "success",
        title: "Enterprise updated",
        message: "Name and slug saved.",
      });
    } catch {
      notify({
        variant: "error",
        title: "Error",
        message: "Failed to update enterprise. Check that the slug is unique.",
      });
    } finally {
      setSavingGeneral(false);
    }
  }

  async function handlePromote() {
    if (!enterpriseId) return;
    setPromoting(true);
    try {
      await updateEnterprise.mutateAsync({ enterpriseId, isDemo: false });
      notify({
        variant: "success",
        title: "Promoted to production",
        message: "This enterprise is now listed under Production.",
      });
      location.reload();
    } catch {
      notify({
        variant: "error",
        title: "Error",
        message: "Failed to promote this enterprise.",
      });
      setPromoting(false);
    }
  }

  async function handleDelete() {
    if (!enterpriseId || !canDelete || !current || !fallbackEnterprise) return;
    setDeleting(true);
    try {
      // You are always on the enterprise being deleted from this page — switch
      // to another accessible enterprise first so the session stays valid.
      await switchEnterprise.mutateAsync(fallbackEnterprise._id);
      await deleteEnterprise.mutateAsync(enterpriseId);
      notify({
        variant: "success",
        title: "Enterprise deleted",
        message: `Deleted ${current.name}. Switched to ${fallbackEnterprise.name}.`,
      });
      location.reload();
    } catch {
      notify({
        variant: "error",
        title: "Error",
        message: `Failed to delete ${current.name}.`,
      });
      setDeleting(false);
    }
  }

  if (enterprisesQuery.isPending) {
    return (
      <div
        data-testid="settings-enterprise-page"
        className="flex h-full flex-col overflow-hidden"
      >
        <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
          <Text size="2" color="gray">
            {t("settings.enterpriseSettingsView.loadingEnterprise")}
          </Text>
        </article>
      </div>
    );
  }

  if (!current || !enterpriseId) {
    return (
      <div
        data-testid="settings-enterprise-page"
        className="flex h-full flex-col overflow-hidden"
      >
        <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
          <Text size="2" color="gray">
            {t("settings.enterpriseSettingsView.noActiveEnterpriseFound")}
          </Text>
        </article>
      </div>
    );
  }

  return (
    <div
      data-testid="settings-enterprise-page"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        <header className="flex flex-col gap-2">
          <Heading as="h1" size="7" weight="bold">
            {t("settings.enterpriseSettingsView.enterprise")}
          </Heading>
          <Text size="2" color="gray">
            Manage identity and lifecycle for the active enterprise (
            {current.name}). Meaningful Gigs only.
          </Text>
        </header>

        {enterpriseId ? (
          <EnterpriseSsoSettingsPanel enterpriseId={enterpriseId} />
        ) : null}

        <Card data-testid="settings-enterprise-general-card">
          <Flex direction="column" gap="3" p="4">
            <Heading as="h2" size="4" weight="medium">
              {t("settings.enterpriseSettingsView.general")}
            </Heading>
            <Text size="2" color="gray">
              Rename the enterprise or change its URL slug. Slugs must be
              unique, lowercase, and use hyphens between words.
            </Text>
            <label htmlFor="enterprise-settings-name">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("common:labels.name")}
              </Text>
              <TextField.Root
                id="enterprise-settings-name"
                data-testid="settings-enterprise-name-input"
                aria-label={t("settings.enterpriseSettingsView.enterpriseName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label htmlFor="enterprise-settings-slug">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("settings.enterpriseSettingsView.slug")}
              </Text>
              <TextField.Root
                id="enterprise-settings-slug"
                data-testid="settings-enterprise-slug-input"
                aria-label={t("settings.enterpriseSettingsView.enterpriseSlug")}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                color={slugInvalid ? "red" : undefined}
              />
              {slugInvalid ? (
                <Text size="1" color="red" mt="1">
                  {t(
                    slugProblem === "length"
                      ? "settings.enterpriseSettingsView.slugMaxLength"
                      : "settings.enterpriseSettingsView.useLowercaseLettersNumbersHyphens",
                  )}
                </Text>
              ) : null}
            </label>
            <Flex>
              <Button
                data-testid="settings-enterprise-save-button"
                aria-label={t(
                  "settings.enterpriseSettingsView.saveEnterpriseGeneralSettings",
                )}
                disabled={!canSaveGeneral}
                loading={savingGeneral}
                onClick={() => void handleSaveGeneral()}
              >
                {t("settings.enterpriseSettingsView.saveChanges")}
              </Button>
            </Flex>
          </Flex>
        </Card>

        {isDemo ? (
          <Card data-testid="settings-enterprise-promote-card">
            <Flex direction="column" gap="3" p="4">
              <Heading as="h2" size="4" weight="medium">
                {t("settings.enterpriseSettingsView.promoteProduction")}
              </Heading>
              <Text size="2" color="gray">
                Move this demo enterprise into the Production list. Demo
                Settings will no longer appear after promote. This only flips
                the list flag — no data is migrated.
              </Text>
              <Flex>
                <Button
                  data-testid="settings-promote-enterprise-button"
                  aria-label={t(
                    "settings.enterpriseSettingsView.promoteProduction",
                  )}
                  loading={promoting}
                  onClick={() => void handlePromote()}
                >
                  {t("settings.enterpriseSettingsView.promoteProduction")}
                </Button>
              </Flex>
            </Flex>
          </Card>
        ) : null}

        <Card data-testid="settings-enterprise-danger-card">
          <Flex direction="column" gap="3" p="4">
            <Heading as="h2" size="4" weight="medium" color="red">
              {t("settings.enterpriseSettingsView.dangerZone")}
            </Heading>
            <Text size="2" color="gray">
              Permanently delete {current.name}. This removes the enterprise for
              everyone, including profiles and access. This cannot be undone.
              {fallbackEnterprise
                ? ` You will be switched to ${fallbackEnterprise.name} first.`
                : t(
                    "settings.enterpriseSettingsView.needAccessAnotherEnterpriseBefore",
                  )}
            </Text>
            {fallbackEnterprise ? (
              <>
                <label htmlFor="enterprise-settings-delete-confirm">
                  <Text as="div" size="2" mb="1" weight="bold">
                    {`Type ${DELETE_CONFIRM_PHRASE} to confirm`}
                  </Text>
                  <TextField.Root
                    id="enterprise-settings-delete-confirm"
                    data-testid="settings-enterprise-delete-confirm-input"
                    aria-label={`Type ${DELETE_CONFIRM_PHRASE} to confirm deletion`}
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder={DELETE_CONFIRM_PHRASE}
                    autoComplete="off"
                  />
                </label>
                <Flex>
                  <Button
                    color="red"
                    data-testid="settings-enterprise-delete-button"
                    aria-label={`Delete enterprise ${current.name}`}
                    disabled={!canDelete}
                    loading={deleting}
                    onClick={() => void handleDelete()}
                  >
                    {t("settings.enterpriseSettingsView.deleteEnterprise")}
                  </Button>
                </Flex>
              </>
            ) : null}
          </Flex>
        </Card>
      </article>
    </div>
  );
}
